import { z } from 'zod';
import {
	ProvinceBoundaryFeatureSchema,
	ProvinceBoundarySchema,
	type ProvinceBoundaryFeature,
	type ProvinceBoundaryFeatureCollection,
} from './provinceBoundary';

/** Khớp `enum TrangThaiDotChuyenDoi` của Prisma (Phase 1). */
export const TrangThaiDotSchema = z.enum(['DRAFT', 'APPLIED', 'REVERTED']);
export type TrangThaiDot = z.infer<typeof TrangThaiDotSchema>;

/** Khớp `enum LoaiChuyenDoiTinh` của Prisma (Phase 1). */
export const LoaiChuyenDoiSchema = z.enum(['RENAME', 'MERGE']);
export type LoaiChuyenDoi = z.infer<typeof LoaiChuyenDoiSchema>;

/** `GET /api/admin/province-conversions` — một phần tử của danh sách. */
export const ConversionBatchSchema = z.object({
	id: z.string(),
	ten: z.string(),
	hieuLucTuNam: z.number(),
	trangThai: TrangThaiDotSchema,
	ghiChu: z.string().nullable(),
	appliedAt: z.string().nullable(),
	appliedBy: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	soAnhXa: z.number(),
}).strip();
export type ConversionBatch = z.infer<typeof ConversionBatchSchema>;

export const ConversionBatchListSchema = z.array(ConversionBatchSchema);

/** Một dòng ánh xạ trong `anhXas` của đợt. */
export const ConversionMappingSchema = z.object({
	maCu: z.string(),
	maMoi: z.string(),
	tenMoi: z.string(),
	loai: LoaiChuyenDoiSchema,
}).strip();
export type ConversionMapping = z.infer<typeof ConversionMappingSchema>;

/** `GET /api/admin/province-conversions/:id/mappings` — chi tiết đợt kèm ánh xạ, đọc ở mọi trạng thái. */
export const ConversionBatchDetailSchema = z.object({
	id: z.string(),
	ten: z.string(),
	trangThai: TrangThaiDotSchema,
	hieuLucTuNam: z.number(),
	anhXas: z.array(ConversionMappingSchema),
}).strip();
export type ConversionBatchDetail = z.infer<typeof ConversionBatchDetailSchema>;

/** `POST /api/admin/province-conversions` — body. */
export const CreateConversionInputSchema = z.object({
	ten: z.string().min(1, 'Tên đợt không được để trống'),
	hieuLucTuNam: z.number().int(),
	ghiChu: z.string().optional(),
});
export type CreateConversionInput = z.infer<typeof CreateConversionInputSchema>;

/** `POST /api/admin/province-conversions/:id/mappings` — kết quả upload CSV. */
export const UploadMappingsResultSchema = z.object({
	count: z.number(),
	warnings: z.array(z.string()),
}).strip();
export type UploadMappingsResult = z.infer<typeof UploadMappingsResultSchema>;

export const PreviewErrorSchema = z.object({
	ma: z.string(),
	thongDiep: z.string(),
	dong: z.number().optional(),
}).strip();
export type PreviewError = z.infer<typeof PreviewErrorSchema>;

export const PreviewStatsSchema = z.object({
	soRename: z.number(),
	soMerge: z.number(),
	soTinhTruoc: z.number(),
	soTinhSau: z.number(),
	soTruongAnhHuong: z.number(),
	soDinhTruoc: z.number(),
	soDinhSau: z.number(),
}).strip();
export type PreviewStats = z.infer<typeof PreviewStatsSchema>;

/** `[minX, minY, maxX, maxY]` — hộp bao của một mã tỉnh sau khi gộp thử. */
export const BboxSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]);
export type Bbox = z.infer<typeof BboxSchema>;

/** `POST /api/admin/province-conversions/:id/preview` — dry-run, KHÔNG có geometry đầy đủ. */
export const PreviewResultSchema = z.object({
	hopLe: z.boolean(),
	loi: z.array(PreviewErrorSchema),
	canhBao: z.array(z.string()),
	thongKe: PreviewStatsSchema,
	bbox: z.record(z.string(), BboxSchema),
}).strip();
export type PreviewResult = z.infer<typeof PreviewResultSchema>;

export const ApplyResultSchema = z.object({
	appliedAt: z.string(),
	appliedBy: z.string(),
	soTinhSau: z.number(),
}).strip();
export type ApplyResult = z.infer<typeof ApplyResultSchema>;

export const RevertResultSchema = z.object({
	soTinhSau: z.number(),
}).strip();
export type RevertResult = z.infer<typeof RevertResultSchema>;

/**
 * Tiến độ SSE cho apply/revert — khớp `conversionProgressSchema` phía backend
 * (`provinceConversionSchema.ts`), cộng `starting` (sự kiện mở luồng, chỉ do
 * controller phát, không qua schema backend — cùng khuôn với `ImportProgress`).
 */
export const ConversionProgressSchema = z.object({
	status: z.enum(['starting', 'reading', 'merging', 'writing', 'completed', 'error']),
	currentRow: z.number(),
	totalRows: z.number(),
	percentage: z.number(),
	message: z.string().optional(),
}).strip();
export type ConversionProgress = z.infer<typeof ConversionProgressSchema>;

/**
 * Nền bản đồ cho `ConversionPreviewMap` — lấy từ
 * `GET /api/provinces/boundaries?ban=hien-hanh` (Phase 4), trả FeatureCollection
 * TRẦN (không bọc `{success,data}`).
 *
 * Định nghĩa CANONICAL nằm ở `schemas/provinceBoundary.ts` (module schema
 * thuần, không phụ thuộc `apiClient`/dịch vụ nào) — re-export tại đây để
 * giữ nguyên tên `BoundaryFeature(Schema|Collection)` cho các nơi đã import
 * từ module này (M4: trước review có HAI bản định nghĩa gần như y hệt ở
 * đây và ở `services/provinceBoundaryApi.ts`, dựng lúc Phase 5/6 chạy song
 * song để tránh đụng file; cả hai phase đã xong nên không cần nhân bản
 * nữa — tách một schema module riêng thay vì để module này phụ thuộc
 * thẳng vào `services/provinceBoundaryApi.ts`, tránh việc mock module
 * dịch vụ đó trong test kéo theo rớt luôn các export schema). KHÔNG import
 * từ `geoJsonApi.ts` (file đó nằm trong danh sách cấm sửa và phục vụ hai
 * file tĩnh khác, không phải endpoint /api này).
 */
export const BoundaryFeatureSchema = ProvinceBoundaryFeatureSchema;
export type BoundaryFeature = ProvinceBoundaryFeature;

export const BoundaryFeatureCollectionSchema = ProvinceBoundarySchema;
export type BoundaryFeatureCollection = ProvinceBoundaryFeatureCollection;
